import {
  _makeWidgetProps,
  BucketFormikInput,
  BucketFormikInputProps,
  asWidgetProps,
} from 'Src/types/widget-helpers';
import React, { ComponentProps, Fragment, useContext, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { Icon } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import { Title } from 'Components/frameworks/title/Title';
import { Tooltip } from 'Components/frameworks/tooltip/Tooltip';
import { ICONS } from 'Components/utils/icon/icons';
import { Text } from 'Components/frameworks/text/Text';
import { useAppContext } from 'Context/ReactAppContext';
import { getKeyValue, t } from 'Src/i18n';
import { saveDonationToProposals } from 'Store/booking/proposals/proposals.action';
import {
  formatAmountForLocale,
  getCurrency,
} from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useDisclosure } from 'Src/hooks/use-disclosure/useDisclosure';
import { RadioButton } from 'Components/frameworks/form-inputs/radio-button/RadioButton';
const DONATION_IMAGE_URL = 'https://ns.clubmed.com/it/2019/be/donation.jpg';
type Props = {
  currency: string;
  isDisabled: boolean;
};
export const Donation = ({ currency, isDisabled }: Props) => {
  const formik = useFormikContext<Record<string, unknown>>();
  const dispatch = useDispatch();
  const { locale } = useAppContext();
  const {
    isOpen: isFreeAmountOpen,
    onOpen: onFreeAmountOpen,
    onClose: onFreeAmountClose,
  } = useDisclosure();
  const { donationAmount } = useContext(PaymentInfoContext);
  const dispatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const options = [
    {
      value: getKeyValue('booking.banking.donation.amount1'),
      formattedValue: formatAmountForLocale(
        getKeyValue('booking.banking.donation.amount1'),
        locale,
        currency,
      ),
    },
    {
      value: getKeyValue('booking.banking.donation.amount2'),
      formattedValue: formatAmountForLocale(
        getKeyValue('booking.banking.donation.amount2'),
        locale,
        currency,
      ),
    },
    {
      value: getKeyValue('booking.banking.donation.amount3'),
      formattedValue: formatAmountForLocale(
        getKeyValue('booking.banking.donation.amount3'),
        locale,
        currency,
      ),
    },
  ];
  const modalTitleId = 'ModalTitleId';
  useEffect(() => {
    if (donationAmount) {
      formik.setFieldValue('donation', +donationAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only pre-fill on mount from existing proposal data, not on every Redux update

  const handleDonationChange = (donation: Partial<FormattedPrice>, debounce = false) => {
    // when the user change his mind and don't want donate it must reset the donation cgu if already checked
    if (!donation.value) {
      formik.setFieldValue('donation', undefined);
      if ((formik.values.termsAndConditions as { donation?: boolean })?.donation) {
        formik.setFieldValue('termsAndConditions.donation', false);
      }
    } else if (debounce) {
      // keep the input responsive while debouncing the price update
      formik.setFieldValue('donation', donation.value);
    }
    if (dispatchTimerRef.current) {
      clearTimeout(dispatchTimerRef.current);
    }
    if (debounce) {
      dispatchTimerRef.current = setTimeout(() => {
        dispatch(saveDonationToProposals(donation as FormattedPrice));
      }, 150);
    } else {
      dispatch(saveDonationToProposals(donation as FormattedPrice));
    }
  };
  return (
    <Fragment>
      <Title
        {...asWidgetProps<ComponentProps<typeof Title>>({
          className: 'mb-10',
          theme: 'title-brand-n2',
        })}
      >
        {t('booking.banking.donation.title')}
      </Title>
      <div className="flex flex-row gap-20 rounded-12 border-1 bg-white p-20 text-14">
        <div>
          <div className="flex w-3/4 md:w-full">
            <p className="mb-15">
              {t('booking.banking.donation.blockTitle')}
              <Tooltip
                {...asWidgetProps<ComponentProps<typeof Tooltip>>({
                  modalProps: {
                    ariaLabel: t('booking.banking.donation.popinTitle'),
                    ariaLabelledby: modalTitleId,
                    closeLabel: t('common.close'),
                    size: 'small',
                  },
                  renderTrigger: (toggle) => (
                    <button className="self-start" onClick={toggle} type="button">
                      <Icon
                        {...asWidgetProps<ComponentProps<typeof Icon>>({
                          name: ICONS.INFORMATION,
                          width: '1.25rem',
                        })}
                      />
                    </button>
                  ),
                })}
              >
                <Image
                  {...asWidgetProps<ComponentProps<typeof Image>>({
                    imageUrl: DONATION_IMAGE_URL,
                    ratioKey: 'L',
                  })}
                />
                <div className="flex flex-col gap-10 p-20">
                  <Title
                    {...asWidgetProps<ComponentProps<typeof Title>>({
                      theme: 'title-brand-n3',
                    })}
                  >
                    {t('booking.banking.donation.popinTitle')}
                  </Title>
                  <Text
                    {...asWidgetProps<ComponentProps<typeof Text>>({
                      text: t('booking.banking.donation.popinDescription'),
                      theme: 'text-sans-n2',
                    })}
                  />
                  <Text
                    {...asWidgetProps<ComponentProps<typeof Text>>({
                      text: t('booking.banking.donation.popinFiscalInfo'),
                      theme: 'text-sans-n2',
                    })}
                  />
                </div>
              </Tooltip>
            </p>
          </div>
          <div className="w-full md:max-w-3/5">
            <div className="mb-15 mt-10 flex w-full cursor-pointer items-center gap-10 text-14">
              <RadioButton
                {...asWidgetProps<ComponentProps<typeof RadioButton>>({
                  isDisabled: isDisabled,
                  onClick: () => {
                    onFreeAmountClose();
                    formik.setFieldValue('donation', 0);
                    handleDonationChange({
                      value: 0,
                    });
                  },
                  dataTestid: 'donation-no-radio',
                  isChecked: !formik.values.donation && !isFreeAmountOpen,
                })}
              />
              <span className="mt-1 inline-block text-14 leading-tight text-black">
                {t('booking.banking.donation.noAmount')}
              </span>
            </div>
            <div className="flex flex-col justify-between md:flex-row">
              {options.map((option) => (
                <div key={option.value}>
                  <BucketFormikInput
                    {...asWidgetProps<BucketFormikInputProps>({
                      isDisabled: isDisabled,
                      label: option.formattedValue,
                      name: 'donation',
                      onChange: () => {
                        onFreeAmountClose();
                        handleDonationChange(option);
                      },
                      type: 'radio',
                      value: option.value,
                    })}
                  />
                </div>
              ))}
            </div>
            <div className="mb-15 mt-10 flex w-full cursor-pointer items-center gap-10 text-14">
              <RadioButton
                {...asWidgetProps<ComponentProps<typeof RadioButton>>({
                  isDisabled: isDisabled,
                  onClick: () => {
                    onFreeAmountOpen();
                    handleDonationChange({
                      value: 0,
                    });
                  },
                  dataTestid: 'donation-free-amount-radio',
                  isChecked: isFreeAmountOpen,
                })}
              />
              <span className="mt-1 inline-block text-14 leading-tight text-black">
                {t('booking.banking.donation.freeAmount')}
              </span>
            </div>
            {isFreeAmountOpen && (
              <div className="ml-0 flex gap-10 md:ml-30">
                <div className="relative">
                  <BucketFormikInput
                    {...asWidgetProps<BucketFormikInputProps>({
                      isDisabled: isDisabled,
                      type: 'number',
                      name: 'freeAmount',
                      dataTestId: 'donation-input',
                      value: formik.values.donation || '',
                      onChange: ({ target: { value } }) =>
                        handleDonationChange(
                          {
                            value: +value,
                          },
                          true,
                        ),
                    })}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2">
                    {getCurrency(locale, currency).symbol}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <Icon
          {...asWidgetProps<ComponentProps<typeof Icon>>({
            className: 'absolute right-5 shrink-0 md:relative md:right-0',
            name: ICONS.DONATION,
            width: '5rem',
          })}
        />
      </div>
    </Fragment>
  );
};
